import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { rmApi } from '../../services/api';
// --- Import Table ---
import { Table } from 'react-bootstrap'; // Assuming Table is needed, add it here
import { FaChartLine, FaUsers, FaHourglassHalf, FaCheckCircle, FaFileInvoiceDollar, FaCalendarAlt, FaMoneyBillWave } from 'react-icons/fa'; // Added FaHourglassHalf, FaCheckCircle, FaFileInvoiceDollar. Kept FaMoneyBillWave just in case, can be removed if not used elsewhere.

// Helper function to format currency (reuse if available elsewhere)
const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '₹0.00';
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long'
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    }).replace(/^(\d+:\d+)(:\d+)?\s*/, '$1 '); // Clean seconds if present
    return `${formattedDate}, ${formattedTime}`;
  } catch (e) {
    return dateString; // Fallback
  }
};

function RMHome() {
  const { getCurrentUser } = useAuth();
  const currentUser = getCurrentUser();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [upcomingPayouts, setUpcomingPayouts] = useState([]);
  const [payoutsLoading, setPayoutsLoading] = useState(true);
  const [payoutsError, setPayoutsError] = useState('');
  
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [invoicesError, setInvoicesError] = useState('');

  // Icons and colors for stats (will be used when API data is available)
  const statsIcons = {
    'total_invoice_amount': { icon: <FaFileInvoiceDollar />, color: '#4299e1' },
    'active_dso_count': { icon: <FaUsers />, color: '#38b2ac' },
    'payout_requested_count': { icon: <FaHourglassHalf />, color: '#f56565' },
    'payout_approved_count': { icon: <FaCheckCircle />, color: '#48bb78' }
  };
  
  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await rmApi.getDashboardStats();
        setStats(response.data);
      } catch (err) {
        console.error("Error fetching dashboard stats:", err);
        setError('Failed to load dashboard statistics. Please try again later.');
        setStats(null); // Reset stats on error
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []); // Fetch stats once on component mount

  // --- useEffect to fetch upcoming payouts ---
  useEffect(() => {
    const fetchUpcomingPayouts = async () => {
      setPayoutsLoading(true);
      setPayoutsError('');
      try {
        const response = await rmApi.getUpcomingPayouts(5); // Fetch top 5
        setUpcomingPayouts(response.data || []);
      } catch (err) {
        console.error("Error fetching upcoming payouts:", err);
        setPayoutsError('Failed to load upcoming payouts.');
        setUpcomingPayouts([]);
      } finally {
        setPayoutsLoading(false);
      }
    };
    fetchUpcomingPayouts();
  }, []); // Fetch once on mount
  
  // --- useEffect to fetch recent invoices ---
  useEffect(() => {
    const fetchRecentInvoices = async () => {
      setInvoicesLoading(true);
      setInvoicesError('');
      try {
        const response = await rmApi.getInvoices({ limit: 6 }); // Fetch last 6 invoices
        const invoicesData = response.data?.items || [];
        
        // Transform the invoice data to match the expected format
        const formattedInvoices = invoicesData.map(invoice => ({
          id: invoice.id,
          dso: invoice.dso_name || invoice.dso_email || 'DSO',
          amount: formatCurrency(invoice.amount),
          date: invoice.created_at || invoice.payment_date,
          status: invoice.invoice_status_str?.toLowerCase() || 'generated'
        }));
        
        setRecentInvoices(formattedInvoices);
      } catch (err) {
        console.error("Error fetching recent invoices:", err);
        setInvoicesError('Failed to load recent invoices.');
        setRecentInvoices([]);
      } finally {
        setInvoicesLoading(false);
      }
    };
    fetchRecentInvoices();
  }, []); // Fetch once on mount
  
  return (
    <div className="rm-home-dashboard">
      <div className="rm-dashboard-welcome">
        <h1>Welcome, {currentUser?.name || 'Relationship Manager'}</h1>
        <p>Here's an overview of your performance and recent activities</p>
      </div>
      
      <div className="rm-stats-grid">
        {loading ? (
          <div className="rm-loading">Loading statistics...</div>
        ) : error ? (
          <div className="rm-error">{error}</div>
        ) : stats ? (
          Object.entries(stats).map(([key, value]) => {
            const statConfig = statsIcons[key] || { 
              icon: <FaChartLine />, 
              color: '#6366f1' 
            };
            
            // Format the value based on the key type
            let formattedValue = value;
            if (key.includes('amount')) {
              formattedValue = formatCurrency(value);
            }
            
            // Convert snake_case to Title Case for display
            const title = key.split('_')
              .map(word => word.charAt(0).toUpperCase() + word.slice(1))
              .join(' ')
              .replace('Count','');
            
            return (
              <div className="rm-stat-card" key={key}>
                <div className="rm-stat-icon" style={{ backgroundColor: statConfig.color }}>
                  {statConfig.icon}
                </div>
                <div className="rm-stat-details">
                  <p className="rm-stat-title">{title}</p>
                  <h3 className="rm-stat-value">{formattedValue}</h3>
                </div>
              </div>
            );
          })
        ) : (
          <div className="rm-no-data">No statistics available</div>
        )}
      </div>
      
      <div className="rm-dashboard-row">
        <div className="rm-dashboard-card rm-dashboard-transactions">
          <div className="rm-card-header">
            <h3>Recent Transactions</h3>
            <button className="rm-view-all">View All</button>
          </div>
          <div className="rm-transaction-list">
            {invoicesLoading ? (
              <div className="rm-loading-small">Loading invoices...</div>
            ) : invoicesError ? (
              <div className="rm-error-small">{invoicesError}</div>
            ) : recentInvoices.length > 0 ? (
              <table>
                <thead>
                  <tr>
                    <th>DSO</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentInvoices.map(invoice => (
                    <tr key={invoice.id}>
                      <td>{invoice.dso}</td>
                      <td>{invoice.amount}</td>
                      <td>{formatDate(invoice.date)}</td>
                      <td>
                        <span className={`rm-status-badge ${invoice.status}`}>
                          {invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="rm-no-data-small">No invoice data available</div>
            )}
          </div>
        </div>

        
        
        <div className="rm-dashboard-card rm-dashboard-calendar">
          <div className="rm-card-header">
            <h3>Upcoming Payouts</h3>
          </div>
          <div className="rm-calendar-content">
            {payoutsLoading ? (
              <div className="rm-loading-small">Loading payouts...</div>
            ) : payoutsError ? (
              <div className="rm-error-small">{payoutsError}</div>
            ) : upcomingPayouts.length > 0 ? (
              upcomingPayouts.map((payout, index) => {
                // Extract date components
                const payoutDate = new Date(payout.requested_at);
                const day = payoutDate.getDate();
                const month = payoutDate.toLocaleString('en-US', { month: 'short' });
                
                // Format amount
                const amount = formatCurrency(payout.total_amount);
                
                // Determine payout type/title
                const payoutTitle = 'Payout ' +payout.status_str
                
                // Determine status text
                const statusText = payout.status_str.toLowerCase() === 'requested' 
                  ? 'Requested' 
                  : payout.status_str.toLowerCase() === 'approved' 
                    ? 'Approved' 
                    : payout.status_str.toLowerCase() === 'pending' 
                      ? 'Pending' 
                      : 'Scheduled';
                
                return (
                  <div className="rm-calendar-event" key={payout.id || index}>
                    <div className="rm-event-date">
                      <span className="rm-event-day">{day}</span>
                      <span className="rm-event-month">{month}</span>
                    </div>
                    <div className="rm-event-info">
                      <h4>{payoutTitle}</h4>
                      <p>{amount} <span className={`rm-payout-status ${payout.status_str}`}>({statusText})</span></p>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="rm-no-data-small">No upcoming payouts</div>
            )}
          </div>
        </div>
      </div>
      
      {/* Add styles for payout statuses and loading states */}
      <style jsx>{`
        .rm-loading-small, .rm-error-small, .rm-no-data-small {
          padding: 20px;
          text-align: center;
          font-size: 0.875rem;
        }
        
        .rm-loading-small {
          color: #6366f1;
        }
        
        .rm-error-small {
          color: #ef4444;
        }
        
        .rm-no-data-small {
          color: #64748b;
        }
        
        .rm-payout-status {
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .rm-payout-status.requested {
          color: #f59e0b;
        }
        
        .rm-payout-status.approved {
          color: #10b981;
        }
        
        .rm-payout-status.pending {
          color: #6366f1;
        }
        
        .rm-payout-status.scheduled {
          color: #8b5cf6;
        }
      `}</style>
      
      <style jsx>{`
        .rm-home-dashboard {
          color: #1e293b;
        }
        
        .rm-dashboard-welcome {
          margin-bottom: 24px;
        }
        
        .rm-dashboard-welcome h1 {
          font-size: 1.5rem;
          font-weight: 600;
          margin: 0 0 8px;
          color: #1e293b;
        }
        
        .rm-dashboard-welcome p {
          margin: 0;
          color: #64748b;
        }
        
        .rm-stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 24px;
          margin-bottom: 32px;
        }
        
        .rm-stat-card {
          background-color: white;
          border-radius: 8px;
          padding: 20px;
          display: flex;
          align-items: center;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        
        .rm-stat-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 6px rgba(0,0,0,0.07);
        }
        
        .rm-stat-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-right: 16px;
          color: white;
          font-size: 1.5rem;
        }
        
        .rm-stat-details {
          flex-grow: 1;
        }
        
        .rm-stat-title {
          margin: 0;
          font-size: 0.875rem;
          color: #050608;
        }
        
        .rm-stat-value {
          margin: 4px 0;
          font-size: 1.5rem;
          font-weight: 600;
        }
        
        .rm-loading,
        .rm-error,
        .rm-no-data {
          grid-column: 1 / -1;
          padding: 20px;
          text-align: center;
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        
        .rm-loading {
          color: #6366f1;
        }
        
        .rm-error {
          color: #ef4444;
        }
        
        .rm-no-data {
          color: #64748b;
        }
        
        .rm-dashboard-row {
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 24px;
        }
        
        .rm-dashboard-card {
          background-color: white;
          border-radius: 8px;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
          overflow: hidden;
        }
        
        .rm-card-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .rm-card-header h3 {
          margin: 0;
          font-size: 1rem;
          font-weight: 600;
        }
        
        .rm-view-all {
          background: none;
          border: none;
          color: #6366f1;
          font-size: 0.875rem;
          cursor: pointer;
          padding: 0;
          font-weight: 500;
        }
        
        .rm-transaction-list {
          padding: 0 20px 16px;
        }
        
        .rm-transaction-list table {
          width: 100%;
          border-collapse: collapse;
        }
        
        .rm-transaction-list th {
          text-align: left;
          padding: 12px 8px;
          font-size: 0.75rem;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: #64748b;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .rm-transaction-list td {
          padding: 12px 8px;
          font-size: 0.875rem;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .rm-transaction-list tr:last-child td {
          border-bottom: none;
        }
        
        .rm-status-badge {
          display: inline-block;
          padding: 2px 8px;
          border-radius: 9999px;
          font-size: 0.75rem;
          font-weight: 500;
        }
        
        .rm-status-badge.approved {
          background-color: #dcfce7;
          color: #15803d;
        }
        
        .rm-status-badge.pending {
          background-color: #fef9c3;
          color: #854d0e;
        }
        
        .rm-status-badge.rejected {
          background-color: #fee2e2;
          color: #b91c1c;
        }
        
        .rm-calendar-content {
          padding: 16px 20px;
        }
        
        .rm-calendar-event {
          display: flex;
          align-items: center;
          padding: 12px 0;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .rm-calendar-event:last-child {
          border-bottom: none;
        }
        
        .rm-event-date {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          width: 48px;
          height: 48px;
          background-color: #f1f5f9;
          border-radius: 8px;
          margin-right: 16px;
          text-align: center;
        }
        
        .rm-event-day {
          font-size: 1rem;
          font-weight: 600;
          color: #1e293b;
        }
        
        .rm-event-month {
          font-size: 0.75rem;
          color: #64748b;
        }
        
        .rm-event-info h4 {
          margin: 0;
          font-size: 0.875rem;
          font-weight: 600;
          color: #1e293b;
        }
        
        .rm-event-info p {
          margin: 4px 0 0;
          font-size: 0.75rem;
          color: #64748b;
        }
        
        /* Responsive adjustments */
        @media screen and (max-width: 992px) {
          .rm-dashboard-row {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
}

export default RMHome;
